import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { useSearchParams } from "react-router-dom";

import { getPerformance } from "@/api/performance";
import { useAuth } from "@/auth/AuthProvider";
import { FilterBar, type FilterSpec } from "@/components/FilterBar";
import { PersonalizableDashboard } from "@/dashboard/PersonalizableDashboard";
import {
  DEFAULT_PERFORMANCE_LAYOUT,
  PERFORMANCE_WIDGETS,
} from "@/dashboard/performance-widgets";
import { PerformanceDataProvider } from "@/dashboard/performance-widgets/PerformanceDataContext";

interface PerformancePageProps {
  editing: boolean;
  onEditingChange: (editing: boolean) => void;
}

export function PerformancePage({ editing, onEditingChange }: PerformancePageProps) {
  const { user } = useAuth();
  const [params, setParams] = useSearchParams();

  function updateFilter(key: string, value: string | null) {
    const next = new URLSearchParams(params);
    if (!value) next.delete(key);
    else next.set(key, value);
    setParams(next);
  }

  function updateFilters(updates: Record<string, string | null>) {
    const next = new URLSearchParams(params);
    for (const [key, value] of Object.entries(updates)) {
      if (!value) next.delete(key);
      else next.set(key, value);
    }
    setParams(next);
  }

  const mission = params.get("mission") ?? undefined;
  const startDatetime = params.get("start_datetime") ?? "";
  const endDatetime = params.get("end_datetime") ?? "";
  const flightLimit = params.get("flight_limit") ?? "";
  const pilotId = params.get("pilot_id") ?? "";

  const queryKey = [
    "performance",
    { mission, startDatetime, endDatetime, flightLimit, pilotId },
  ];
  const { data, isFetching, isError, error } = useQuery({
    queryKey,
    queryFn: () =>
      getPerformance({
        mission,
        start_datetime: startDatetime || undefined,
        end_datetime: endDatetime || undefined,
        flight_limit: flightLimit || undefined,
        pilot_id: pilotId || undefined,
      }),
    enabled: !!user,
    placeholderData: keepPreviousData,
  });

  if (isError) {
    return (
      <div className="callout danger">
        <strong>Could not load performance</strong>
        <p>{error instanceof Error ? error.message : "Unknown error"}</p>
      </div>
    );
  }

  if (!user) return null;

  const filters = buildFilters({
    missions: data?.missions ?? [],
    selectedMission: data?.selected_mission ?? mission ?? "",
    startDatetime: toInputDatetime(data?.filters.start_datetime) || startDatetime,
    endDatetime: toInputDatetime(data?.filters.end_datetime) || endDatetime,
    flightLimit: data?.filters.flight_limit
      ? String(data.filters.flight_limit)
      : flightLimit,
    canPickPilot: data?.can_pick_pilot ?? false,
    pilotId: data?.filters.pilot_id ? String(data.filters.pilot_id) : pilotId,
    pilots: data?.pilots ?? [],
  });

  return (
    <PerformanceDataProvider data={data ?? null} isLoading={isFetching && !data}>
      <PersonalizableDashboard
        pageKey="performance"
        widgets={PERFORMANCE_WIDGETS}
        accountType={user.account_type}
        defaultActiveTypes={DEFAULT_PERFORMANCE_LAYOUT}
        editing={editing}
        onEditingChange={onEditingChange}
        title="Performance"
        toolbar={
          <FilterBar
            filters={filters}
            onChange={(key, value) => updateFilter(key, value || null)}
            onChangeMany={updateFilters}
            trailing={
              isFetching ? (
                <span className="perf-page__loading">
                  {data ? "Updating…" : "Loading…"}
                </span>
              ) : data ? (
                <span className="perf-page__summary">
                  {data.summary.flight_count} / {data.summary.total_flight_count} ·{" "}
                  {data.summary.window_label}
                </span>
              ) : null
            }
          />
        }
      />
    </PerformanceDataProvider>
  );
}

/**
 * Convert an ISO-8601 datetime (e.g. ``2026-04-16T15:18:00``) into the value
 * expected by `<input type="datetime-local">` (no seconds, no timezone).
 */
function toInputDatetime(value: string | null | undefined): string {
  if (!value) return "";
  // Trim anything after the minutes so HTML datetime-local accepts it. Also
  // strip a trailing "Z" or offset — the backend returns naive datetimes but
  // we want to be defensive here.
  const trimmed = value.replace(/(Z|[+-]\d\d:\d\d)$/, "");
  const match = trimmed.match(/^(\d{4}-\d{2}-\d{2}T\d{2}:\d{2})/);
  return match ? match[1] : "";
}

function buildFilters({
  missions,
  selectedMission,
  startDatetime,
  endDatetime,
  flightLimit,
  canPickPilot,
  pilotId,
  pilots,
}: {
  missions: string[];
  selectedMission: string;
  startDatetime: string;
  endDatetime: string;
  flightLimit: string;
  canPickPilot: boolean;
  pilotId: string;
  pilots: { id: number; username: string; flight_count: number }[];
}): FilterSpec[] {
  const out: FilterSpec[] = [];

  if (canPickPilot) {
    out.push({
      type: "select",
      key: "pilot_id",
      label: "Pilot",
      value: pilotId,
      disabled: pilots.length === 0,
      options: [
        { value: "", label: "All pilots" },
        ...pilots.map((p) => ({
          value: String(p.id),
          label: `${p.username} (${p.flight_count})`,
        })),
      ],
    });
  }

  out.push({
    type: "select",
    key: "mission",
    label: "Mission",
    value: selectedMission,
    disabled: missions.length === 0,
    options:
      missions.length === 0
        ? [{ value: "", label: "No missions" }]
        : missions.map((m) => ({ value: m, label: m })),
  });

  out.push({
    type: "daterange",
    key: "window",
    label: "Window",
    startKey: "start_datetime",
    endKey: "end_datetime",
    startValue: startDatetime,
    endValue: endDatetime,
    withTime: true,
  });

  out.push({
    type: "number",
    key: "flight_limit",
    label: "Limit",
    value: flightLimit,
    min: 1,
    placeholder: "Auto",
  });

  return out;
}
